import { useState, useCallback, useContext } from "react";

import dayjs from "dayjs";

import {
  faCheck,
  faQuestionCircle,
  faReceipt,
  faClockRotateLeft,
  faShareNodes,
  faCircleQuestion,
  faCircleCheck,
  faBan
} from "@fortawesome/free-solid-svg-icons";
import { faEye } from "@fortawesome/pro-solid-svg-icons";

import {
  sumTonsCo2e,
  splitAggregatedTransactionsBreakdownSubcategories
} from "@aclymatepackages/other-helpers";
import mainTheme from "@aclymatepackages/themes";
import findSubcategory from "@aclymatepackages/subcategories";

import {
  useAccountData,
  useCollectionDataListener,
  useMutateCachedCollection,
  auth
} from "../firebase";
import { fetchOurApi } from "../utils/apiCalls";
import { getAccountId } from "../accountHelpers";
import { analyticsTrack } from "../analytics";
import usePlatformLayoutContext from "../contexts/platformLayout";
import useExplorerLayoutContext from "../contexts/explorerLayout";
import usePermissions from "../hooks/usePermissions";
import { AdminDataContext } from "../contexts/adminDataContext";
import useSimulationContext from "../contexts/simulationContext";

// The single write path for vendor-relationships field updates that used to
// be direct client Firestore writes -- firestore.rules now denies client
// create/update/delete on that collection outright (see PR that added this).
// The backend enforces which party (buyer or vendor) may write which field;
// `collectionAlias` only controls which local SWR cache slot gets
// revalidated after the write ("customers" or "vendor-relationships" query
// the same underlying collection under different cache keys).
//
// Simulation mode and admin view mode never had a real backend write to
// begin with -- useCachedFirebaseCrud's updateCollectionDoc short-circuited
// both before ever touching Firestore, applying the update to local
// in-memory state instead (SimulationContext's updateCollection / admin's
// editAdminData). This hook is the sole replacement for every one of those
// calls on vendor-relationships, so it has to preserve both branches --
// otherwise a simulated demo account or an admin previewing another
// company's data would call the real endpoint with a fixture/foreign id
// the backend doesn't recognize, and the edit would silently fail instead
// of updating the local preview the way it always did.
export const useUpdateVendorRelationshipFields = (
  collectionAlias = "vendor-relationships"
) => {
  const mutateCachedCollection = useMutateCachedCollection();
  const { viewMode, isSimulation } = usePlatformLayoutContext() || {};
  const { updateCollection } = useSimulationContext() || {};
  const { adminData, editAdminData } = useContext(AdminDataContext) || {};

  const isViewModeNotCompany = !!viewMode && viewMode !== "company";

  return async (relationshipId, updates) => {
    const applyUpdates = (currentData) => {
      if (!currentData) {
        return currentData;
      }
      return currentData.map((doc) =>
        doc.id === relationshipId ? { ...doc, ...updates } : doc
      );
    };

    if (isSimulation) {
      updateCollection?.(collectionAlias, applyUpdates);
      return { success: true };
    }

    if (isViewModeNotCompany) {
      const currentViewModeData = adminData?.[collectionAlias];
      editAdminData?.(collectionAlias, applyUpdates(currentViewModeData));
      return { success: true };
    }

    const result = await fetchOurApi({
      path: "/vendors/update-vendor-relationship-fields",
      method: "POST",
      data: { relationshipId, updates },
      callback: (res) => res
    });

    await mutateCachedCollection(collectionAlias);

    return result;
  };
};

export const EARLIEST_BUYER_SURVEY_YEAR = 2021;

export const vendorStatuses = {
  confirmed: {
    id: "confirmed",
    icon: faCheck,
    severity: 0,
    tooltip: "This vendor is confirmed and has confirmed transactions",
    name: "Confirmed Vendors",
    color: mainTheme.palette.secondary.main
  },
  uncategorized: {
    id: "uncategorized",
    severity: 2,
    tooltip:
      "This vendor hasn't been categorized so their emissions haven't been added to your balance.",
    name: "Uncategorized Vendors",
    icon: faQuestionCircle,
    color: mainTheme.palette.error.main
  },
  noTransactions: {
    id: "noTransactions",
    icon: faReceipt,
    tooltip:
      "This vendor has been categorized but doesn't have any transactions",
    name: "No transactions",
    color: mainTheme.palette.error.light,
    severity: 1
  },
  unconfirmed: {
    id: "unconfirmed",
    tooltip:
      "This vendor hasn't been confirmed yet so their emissions aren't as accurate as they could be.",
    icon: faEye,
    name: "Unconfirmed",
    color: mainTheme.palette.error.dark,
    severity: 3
  }
};

export const surveyDataStatusMap = {
  requested: {
    label: "Requested",
    icon: faClockRotateLeft,
    color: mainTheme.palette.warning.dark,
    chipColor: "warning",
    tooltip: "You've requested this vendor's emissions data."
  },
  pending: {
    label: "Shared — awaiting confirmation",
    icon: faShareNodes,
    color: mainTheme.palette.info.main,
    chipColor: "info",
    tooltip: "This vendor has shared their emissions data. Review and accept."
  },
  unconfirmed: {
    label: "Unconfirmed",
    icon: faCircleQuestion,
    color: mainTheme.palette.text.secondary,
    chipColor: "default",
    tooltip: "Survey completed but data not yet shared."
  },
  accepted: {
    label: "Accepted",
    icon: faCircleCheck,
    color: mainTheme.palette.secondary.main,
    chipColor: "success",
    tooltip: "You've accepted this vendor's emissions data."
  },
  revoked: {
    label: "Revoked",
    icon: faBan,
    color: mainTheme.palette.error.main,
    chipColor: "error",
    tooltip: "This vendor has revoked their shared emissions data."
  }
};

export const updateSurveyDataEntry = (surveyData, year, updates) => {
  const existing = (surveyData || []).find(
    (entry) => Number(entry.year) === Number(year)
  );
  if (existing) {
    return (surveyData || []).map((entry) =>
      Number(entry.year) === Number(year) ? { ...entry, ...updates } : entry
    );
  }
  return [...(surveyData || []), { year: Number(year), ...updates }];
};

export const getAvailableSurveyYears = (vendorSurveys = []) => {
  const lastCompleteYear = dayjs().year() - 1;
  if (!vendorSurveys.length) {
    return [lastCompleteYear];
  }
  const completedYears = vendorSurveys.map(({ year }) => Number(year));
  const minYear = Math.min(...completedYears, lastCompleteYear);
  const maxYear = Math.max(...completedYears, lastCompleteYear);
  return Array.from({ length: maxYear - minYear + 1 }, (_, i) => maxYear - i);
};

export const getAggregateSurveyStatus = (surveyData = []) => {
  const nonRevokedEntry = [...surveyData]
    .sort((a, b) => Number(b.year) - Number(a.year))
    .find(({ status }) => status && status !== "revoked");
  if (nonRevokedEntry) return nonRevokedEntry.status;
  const hasAnyEntry = surveyData.some(({ status }) => !!status);
  if (hasAnyEntry) return "revoked";
  return null;
};

export const getSurveyYearsForBuyer = (startDate, accountingYears = []) => {
  const companyStartYear = startDate
    ? dayjs(startDate?.toDate ? startDate.toDate() : startDate).year()
    : EARLIEST_BUYER_SURVEY_YEAR;
  const startYear = Math.max(companyStartYear, EARLIEST_BUYER_SURVEY_YEAR);
  const currentYear = dayjs().year();
  const lockedYears = new Set(
    accountingYears.map(({ date }) => {
      const d = date?.toDate ? date.toDate() : date;
      return dayjs(d).year();
    })
  );
  return Array.from(
    { length: Math.max(currentYear - startYear, 0) },
    (_, i) => currentYear - 1 - i
  ).filter((year) => !lockedYears.has(year));
};

export const deriveSurveyCompletion = ({ vendorSurveys = [] } = {}) => {
  const completedYears = vendorSurveys.map(({ year: y }) => Number(y));
  const mostRecentSurveyYear = completedYears.length
    ? Math.max(...completedYears)
    : null;
  const availableYears = [...completedYears].sort((a, b) => b - a);
  const hasSurveyData = mostRecentSurveyYear !== null;
  return {
    hasSurveyData,
    mostRecentSurveyYear,
    availableYears
  };
};

export const buildSurveyDataFromVendorSurvey = (vendorSurvey) => {
  const { tonsCo2ePerDollar, emissionCategory, industryNaics, industryName } =
    vendorSurvey;
  return {
    tonsCo2ePerDollar,
    emissionCategory,
    naicsCode: industryNaics,
    naicsTitle: industryName
  };
};

export const isVendorSurveyYearIncomplete = (vendorSurveys, year) => {
  const numericYear = Number(year);
  const vendorSurvey = (vendorSurveys || []).find(
    ({ year: y }) => Number(y) === numericYear
  );
  if (!vendorSurvey) {
    return false;
  }
  const skippedFields = vendorSurvey.skippedFields || [];
  const unknownFields = vendorSurvey.unknownFields || [];
  return skippedFields.length > 0 || unknownFields.length > 0;
};

// A company that already has a paid Navigator subscription owns its
// emissions data through its own accounting/QuickBooks sync, not through the
// Explorer survey -- even for a year it's never run that survey for. Sending
// it into the survey flow would hit onExplorerSurveySubmit's unconditional
// delete-then-recreate of transactions/aggregation docs
// (functions/controllers/vendors.js), which doesn't check closed accounting
// years. Mirrors functions/utils/helperFunctions.js's
// findEffectiveStripeCustomerId -- covers the legacy billing.stripeCustomerId
// path too (see docs/FIRESTORE_SCHEMA.md's note on that fallback).
export const hasEffectiveStripeCustomerId = (accountData) =>
  !!(accountData?.stripeCustomerId || accountData?.billing?.stripeCustomerId);

const sharePaidAccountYear = async ({
  relationshipId,
  year,
  revenueYears,
  buyerLabel,
  activateSnackbar,
  mutateCachedCollection
}) => {
  const hasRevenueForYear = (revenueYears || []).some(
    ({ year: y, revenue }) => Number(y) === year && !!revenue
  );
  if (!hasRevenueForYear) {
    activateSnackbar?.({
      severity: "warning",
      message: `Set your ${year} revenue before sharing this year's data with ${buyerLabel}.`
    });
    return;
  }
  // year/viaPaidAccount only -- relationshipId is a raw Firestore doc ID and
  // AGENTS.md's analytics rule prohibits passing raw identifiers to
  // analyticsTrack.
  analyticsTrack("scout_customer_share_clicked", {
    year,
    viaPaidAccount: true
  });
  try {
    const result = await fetchOurApi({
      path: "/vendors/share-paid-account-survey-data",
      method: "POST",
      data: { relationshipId, year, share: true },
      callback: (res) => res
    });
    if (result?.error) {
      activateSnackbar?.({
        severity: "error",
        message: result.message || `Couldn't share with ${buyerLabel}. Try again?`
      });
      return;
    }
    if (result?.surveyData) {
      // surveyData is computed and written server-side by
      // share-paid-account-survey-data -- revalidate the cache instead of
      // writing the response back directly. The backend has already
      // committed the write (and emailed the buyer) by this point, so a
      // revalidation failure here must not surface as a share failure --
      // that would invite a retry that re-sends the buyer notification for
      // an already-successful share.
      try {
        await mutateCachedCollection("customers");
      } catch (cacheErr) {
        // A minimal projection only -- AGENTS.md's analytics/logging rule
        // prohibits passing a raw error object (which could carry request
        // config/headers) to console.error.
        console.error(
          "[shareCustomerYear] paid-account cache refresh failed after a successful share",
          { message: cacheErr?.message, name: cacheErr?.name }
        );
      }
    }
    activateSnackbar?.({
      severity: "success",
      message: `Shared your ${year} emissions data with ${buyerLabel}. They can revoke or you can revoke any time.`
    });
  } catch (err) {
    console.error("[shareCustomerYear] paid-account backend call threw", {
      message: err?.message,
      name: err?.name
    });
    activateSnackbar?.({
      severity: "error",
      message: `Couldn't share with ${buyerLabel}. Check your connection and try again.`
    });
  }
};

export const useShareCustomerYear = () => {
  const [accountData] = useAccountData();
  const [customerRelationships] = useCollectionDataListener("customers");
  const { activateSnackbar } = useExplorerLayoutContext() || {};
  const { canShare } = usePermissions();
  const mutateCachedCollection = useMutateCachedCollection();

  return useCallback(
    async ({ relationshipId, year }) => {
      if (!canShare) {
        activateSnackbar?.({
          severity: "error",
          message: "You don't have permission to share data with customers."
        });
        return;
      }
      if (!relationshipId || year === undefined || year === null) {
        activateSnackbar?.({
          severity: "error",
          message: "Couldn't share — missing relationship or year."
        });
        return;
      }
      const numericYear = Number(year);
      const relationship = (customerRelationships || []).find(
        ({ id }) => id === relationshipId
      );
      if (!relationship) {
        activateSnackbar?.({
          severity: "error",
          message: "Couldn't find that customer. Refresh and try again?"
        });
        return;
      }
      const buyerLabel = relationship.buyerName || "your customer";
      if (hasEffectiveStripeCustomerId(accountData)) {
        return sharePaidAccountYear({
          relationshipId,
          year: numericYear,
          revenueYears: accountData?.revenueYears,
          buyerLabel,
          activateSnackbar,
          mutateCachedCollection
        });
      }
      const vendorSurveys = accountData?.vendorSurveys || [];
      const vendorSurvey = vendorSurveys.find(
        ({ year: y }) => Number(y) === numericYear
      );
      const isSurveyCompleteForYear =
        vendorSurvey?.tonsCo2ePerDollar !== undefined;
      if (!isSurveyCompleteForYear) {
        window.location.href = `/explorer/emissions-survey?year=${numericYear}`;
        return;
      }
      if (isVendorSurveyYearIncomplete(vendorSurveys, numericYear)) {
        activateSnackbar?.({
          severity: "warning",
          message: `Your ${numericYear} survey still has skipped or unknown answers. Complete it before sharing with ${buyerLabel}.`
        });
        return;
      }
      const existingSurveyData = relationship.surveyData || [];
      const existingEntry = existingSurveyData.find(
        ({ year: y }) => Number(y) === numericYear
      );
      if (existingEntry?.status === "accepted") {
        activateSnackbar?.({
          severity: "info",
          message: `${buyerLabel} has already accepted your ${numericYear} share.`
        });
        return;
      }
      if (existingEntry?.status === "pending") {
        activateSnackbar?.({
          severity: "info",
          message: `Your ${numericYear} data is already shared with ${buyerLabel} — they're reviewing it.`
        });
        return;
      }
      analyticsTrack("scout_customer_share_clicked", {
        relationshipId,
        year: numericYear
      });
      // surveyData is computed and written server-side by
      // share-vendor-survey-data (it's business-logic-computed, not a plain
      // client-owned field); useCollectionDataListener's onSnapshot picks up
      // that write automatically, so there's no local write to make or
      // revert here.
      try {
        const response = await fetchOurApi({
          path: "/vendors/share-vendor-survey-data",
          method: "POST",
          data: { relationshipId, year: numericYear, share: true },
          callback: (res) => res
        });
        if (!response?.success) {
          activateSnackbar?.({
            severity: "error",
            message: `Couldn't share with ${buyerLabel}. Try again?`
          });
          return;
        }
        activateSnackbar?.({
          severity: "success",
          message: `Shared your ${numericYear} emissions data with ${buyerLabel}. They can revoke or you can revoke any time.`
        });
      } catch (err) {
        console.error("[shareCustomerYear] backend call threw", {
          message: err?.message,
          name: err?.name
        });
        activateSnackbar?.({
          severity: "error",
          message: `Couldn't share with ${buyerLabel}. Check your connection and try again.`
        });
      }
    },
    [
      canShare,
      customerRelationships,
      accountData,
      activateSnackbar,
      mutateCachedCollection
    ]
  );
};

const findVendorStatus = ({
  emissionCategory,
  isConfirmed,
  tonsCo2ePerDollar,
  scope,
  naicsCode
}) => {
  if (!isConfirmed) {
    return vendorStatuses.unconfirmed;
  }

  const isVendorUncategorized = () => {
    if (!emissionCategory) {
      return true;
    }

    if (emissionCategory === "spend-based") {
      return !naicsCode || !tonsCo2ePerDollar;
    }

    if (scope === 3 && !tonsCo2ePerDollar) {
      return true;
    }

    return false;
  };

  if (isVendorUncategorized()) {
    return vendorStatuses.uncategorized;
  }

  return vendorStatuses.confirmed;
};

export const formatVendors = ({
  vendorsWithRelationships,
  matchedVendorTransactions,
  transactionsMonthsData,
  emissionsSum
}) => {
  const findVendorSurveyStatus = (
    survey,
    surveyData,
    vendorContacts,
    hasExplorerAccount
  ) => {
    const aggregateStatus = getAggregateSurveyStatus(surveyData);

    if (aggregateStatus) {
      return `survey-${aggregateStatus}`;
    }

    const { dateCompleted } = survey || {};

    if (dateCompleted) {
      return "survey-unconfirmed";
    }

    const anyContactInvited = (vendorContacts || []).some(
      ({ explorerInvitedAt }) => !!explorerInvitedAt
    );
    const isInvited = anyContactInvited || (survey && !dateCompleted);

    if (isInvited && hasExplorerAccount) {
      return "has-explorer-account";
    }

    if (isInvited) {
      return "invited";
    }

    return "not-invited";
  };

  const extractAndSumVendorTransactions = (aggregatedTransactions) => {
    const vendorTransactions =
      splitAggregatedTransactionsBreakdownSubcategories(aggregatedTransactions);

    const totalTransactionsCount = vendorTransactions.reduce(
      (acc, { transactionsCount }) => acc + transactionsCount,
      0
    );

    const tonsCo2e = sumTonsCo2e(vendorTransactions);

    return {
      transactions: vendorTransactions,
      transactionsCount: totalTransactionsCount,
      tonsCo2e,
      avgTonsCo2ePerTransaction: tonsCo2e / (totalTransactionsCount || 1)
    };
  };

  const formatVendorsWithRelationships = (relationship) => {
    const {
      id: relationshipId,
      vendorId,
      vendorName,
      nickname,
      vendorData,
      survey,
      surveyData,
      vendorContacts,
      hasExplorerAccount,
      ...otherProps
    } = relationship;
    const { scopeThreeCategory = 1 } = vendorData || {};

    const { aggregatedTransactions = [] } =
      matchedVendorTransactions.find(
        ({ companyId }) => companyId === vendorId
      ) || {};

    const vendorTransactionProps = extractAndSumVendorTransactions(
      aggregatedTransactions
    );

    const displayName = getVendorDisplayName({ name: vendorName, nickname });

    return {
      ...otherProps,
      id: relationshipId,
      name: displayName,
      canonicalName: vendorName,
      nickname,
      vendorId,
      hasExplorerAccount,
      surveyStatus: findVendorSurveyStatus(
        survey,
        surveyData,
        vendorContacts,
        hasExplorerAccount
      ),
      vendorSurveyStatus: findVendorSurveyStatus(
        survey,
        surveyData,
        vendorContacts,
        hasExplorerAccount
      ),
      survey,
      surveyData,
      vendorContacts,
      ...{ ...vendorData, scopeThreeCategory },
      ...vendorTransactionProps
    };
  };

  const formattedVendors = vendorsWithRelationships.map((relationship) => {
    const formattedVendor = formatVendorsWithRelationships(relationship);

    const { id: status, severity } = findVendorStatus(formattedVendor);

    return {
      ...formattedVendor,
      status,
      severity
    };
  });

  const totalAggregatedTransactionsCount = transactionsMonthsData.reduce(
    (sum, { transactionsCount = 0 }) => sum + transactionsCount,
    0
  );

  const vendorsWithTransactions = formattedVendors.filter(
    ({ transactionsCount }) => transactionsCount
  );

  const totalVendorTonsCo2e = vendorsWithTransactions.reduce(
    (sum, { tonsCo2e }) => sum + tonsCo2e,
    0
  );

  return formattedVendors.map((vendor) => {
    const { transactionsCount, tonsCo2e } = vendor;

    return {
      ...vendor,
      transactionsCountPercentage: transactionsCount
        ? (transactionsCount / totalAggregatedTransactionsCount) * 100
        : 0,
      totalTonsCo2ePercentage: (tonsCo2e / emissionsSum) * 100,
      typeTonsCo2ePercentage: (tonsCo2e / totalVendorTonsCo2e) * 100
    };
  });
};

export const archiveVendorTransactions = async (vendorId) => {
  const companyId = getAccountId();

  return await fetchOurApi({
    path: "/transactions/archive-vendor-transactions",
    method: "POST",
    data: {
      companyId,
      vendorId
    }
  });
};

export const useOnVendorSave = (callback = () => {}) => {
  const updateVendorRelationshipFields = useUpdateVendorRelationshipFields();
  const { triggerTransactionUpdateCountdown } = usePlatformLayoutContext();

  const [saveLoading, setSaveLoading] = useState(false);

  const onVendorSave = async (vendor) => {
    const {
      emissionCategory,
      scopeThreeCategory,
      naicsCode,
      naicsTitle,
      id,
      tonsCo2ePerDollar: vendorTonsCo2ePerDollar
    } = vendor;

    const companyId = getAccountId();

    const updateScopeProps = () => {
      if (emissionCategory === "spend-based") {
        return { scopeThreeCategory, scope: 3 };
      }

      const { scope, scopeThreeCategory: subcategoryScopeThreeCategory } =
        findSubcategory(emissionCategory);

      if (scope === 3) {
        return { scopeThreeCategory: subcategoryScopeThreeCategory, scope };
      }
      return { scopeThreeCategory: null, scope };
    };

    const buildEmissionsCategoriesProps = () => {
      if (emissionCategory === "spend-based") {
        return {
          naicsCode,
          naicsTitle,
          tonsCo2ePerDollar: vendorTonsCo2ePerDollar
        };
      }

      const { naics } = findSubcategory(emissionCategory);
      const { tonsCo2ePerDollar } = naics || {};
      return { tonsCo2ePerDollar };
    };

    const vendorData = {
      emissionCategory,
      ...updateScopeProps(),
      ...buildEmissionsCategoriesProps(),
      isConfirmed: true,
      confirmedBy: "user",
      confirmedAt: new Date(),
      confirmedByUserId: auth.currentUser?.uid || null
    };

    setSaveLoading(true);

    await updateVendorRelationshipFields(id, { vendorData });

    await fetchOurApi({
      path: "/transactions/update-vendor-transactions",
      method: "POST",
      data: {
        companyId,
        ...vendor,
        ...vendorData
      },
      callback: () => triggerTransactionUpdateCountdown()
    });

    return callback();
  };

  return { saveLoading, onVendorSave, setSaveLoading };
};

export const isVendorUncategorized = ({ emissionCategory, naicsCode }) => {
  if (!emissionCategory) {
    return true;
  }
  if (emissionCategory === "spend-based") {
    return !naicsCode;
  }
  return false;
};

export const useOnVendorUncategorize = (callback = () => {}) => {
  const updateVendorRelationshipFields = useUpdateVendorRelationshipFields();
  const { triggerTransactionUpdateCountdown } = usePlatformLayoutContext();

  const [uncategorizeLoading, setUncategorizeLoading] = useState(false);

  const onVendorUncategorize = async (vendor) => {
    const { id, vendorId } = vendor;
    const companyId = getAccountId();

    const clearedVendorData = {
      emissionCategory: null,
      naicsCode: null,
      naicsTitle: null,
      scopeThreeCategory: null,
      tonsCo2ePerDollar: 0,
      isConfirmed: true,
      uncategorizedBy: "user",
      uncategorizedAt: new Date(),
      uncategorizedByUserId: auth.currentUser?.uid || null
    };

    setUncategorizeLoading(true);

    await updateVendorRelationshipFields(id, {
      vendorData: clearedVendorData
    });

    await fetchOurApi({
      path: "/transactions/uncategorize-vendor-transactions",
      method: "POST",
      data: {
        companyId,
        vendorId,
        id
      },
      callback: () => triggerTransactionUpdateCountdown()
    });

    return callback();
  };

  return {
    uncategorizeLoading,
    onVendorUncategorize,
    setUncategorizeLoading
  };
};

// Utility function to get the display name for a vendor
// Returns nickname if available, otherwise returns the canonical name
export const getVendorDisplayName = (vendor) => {
  if (!vendor) {
    return "";
  }
  return vendor.nickname || vendor.name || "";
};

// Utility function to get both display name and canonical name for contexts where both are needed
export const getVendorNames = (vendor) => {
  if (!vendor) return { displayName: "", canonicalName: "" };
  return {
    displayName: vendor.nickname || vendor.name || "",
    canonicalName: vendor.name || "",
    nickname: vendor.nickname || null
  };
};
